import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Breadcrumb } from "@/components/Common/Breadcrumb";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useGetMyOrganization } from "@/hooks/useOrganizations";
import { useGetPlans } from "@/hooks/usePlans";
import {
  ENTERPRISE_PLAN_NAME,
  FREE_PLAN_NAME,
  PRO_PLAN_NAME,
} from "@/lib/constants";
import { Check, CreditCard, Crown, Zap } from "lucide-react";
import { useMemo, useState } from "react";
import { StripeCheckoutDialog } from "./StripeCheckoutDialog";

export function PlansPage() {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const orgQuery = useGetMyOrganization();
  const currentPlan = orgQuery.data.plan;
  const planQuery = useGetPlans();
  const planIcons = {
    [FREE_PLAN_NAME]: Zap,
    [PRO_PLAN_NAME]: CreditCard,
    [ENTERPRISE_PLAN_NAME]: Crown,
  };

  const planFeatures = {
    [FREE_PLAN_NAME]: [
      "1 Team Member",
      "5 Applications/month",
      "Basic Support",
      "50GB Bandwitdh",
      "Community Access",
    ],
    [PRO_PLAN_NAME]: [
      "10 Team Members",
      "Unlimited Applications",
      "Priority Support",
      "50GB Storage",
      "Advanced Analytics",
      "Custom Domains",
    ],
    [ENTERPRISE_PLAN_NAME]: [
      "100 Team Members",
      "Unlimited Everything",
      "24/7 Premium Support",
      "500GB Storage",
      "Advanced Analytics",
      "Custom Domains",
      "SSO Integration",
      "Dedicated Account Manager",
    ],
  };

  const handleSelectPlan = (planId: string) => {
    setSelectedPlan(planId);
    setShowCheckout(true);
  };

  const breadcrumbs = useMemo(() => {
    return [{ label: "plans" }];
  }, []);

  return (
    <div className="min-h-full">
      <div className="hidden lg:block border-b border-slate-800 bg-slate-900/90">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-5">
          <Breadcrumb items={breadcrumbs} />
        </div>
      </div>
      <div className="p-4 lg:p-8 space-y-6 lg:space-y-8">
        {/* Header */}
        <div className="mb-6 lg:mb-8 pt-12 lg:pt-0">
          <h1 className="text-3xl lg:text-4xl font-bold text-cyan-400  mb-2">
            Plans & Billing
          </h1>
          <p className="text-slate-300  text-sm lg:text-base">
            Choose the perfect plan for your team's needs
          </p>
        </div>

        {/* Current Plan */}
        <Card className="holo-card">
          <CardHeader>
            <CardTitle className="text-cyan-400  text-lg lg:text-xl">
              Current Plan
            </CardTitle>
            <CardDescription className="text-slate-400  text-sm">
              You are currently on the {currentPlan?.name} plan
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 lg:h-12 lg:w-12 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center flex-shrink-0">
                  {currentPlan &&
                    planIcons[currentPlan.name as keyof typeof planIcons] &&
                    (() => {
                      const Icon =
                        planIcons[currentPlan.name as keyof typeof planIcons];
                      return (
                        <Icon className="h-5 w-5 lg:h-6 lg:w-6 text-cyan-400" />
                      );
                    })()}
                </div>
                <div>
                  <h3 className="font-semibold text-cyan-100  text-sm lg:text-base">
                    {currentPlan?.name}
                  </h3>
                  <p className="text-xs lg:text-sm text-slate-400 ">
                    ${(currentPlan?.costPerUserCents || 0) / 100}/month per user
                  </p>
                </div>
              </div>
              <Badge className="text-cyan-400 bg-cyan-400/10 border-cyan-400/30  text-xs lg:text-sm w-fit">
                Current Plan
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Available Plans */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
          {planQuery.data.map((plan) => {
            const Icon = planIcons[plan.name as keyof typeof planIcons];
            const features =
              planFeatures[plan.name as keyof typeof planFeatures];
            const isCurrentPlan = plan.id === currentPlan?.id;

            return (
              <Card key={plan.id} className={`holo-card relative`}>
                <CardHeader className="text-center pb-4">
                  <div className="flex justify-center mb-4">
                    <div
                      className={`h-12 w-12 lg:h-16 lg:w-16 rounded-full flex items-center justify-center border-2 ${"bg-slate-800/50 border-slate-600 text-slate-400"}`}
                    >
                      <Icon className="h-6 w-6 lg:h-8 lg:w-8" />
                    </div>
                  </div>
                  <CardTitle className="text-lg lg:text-xl text-cyan-100 ">
                    {plan.name}
                  </CardTitle>
                  <CardDescription className="text-slate-400  text-xs lg:text-sm">
                    {plan.description}
                  </CardDescription>
                  <div className="mt-4">
                    <span className="text-3xl lg:text-4xl font-bold text-cyan-100 ">
                      ${plan.costPerUserCents / 100}
                    </span>
                    <span className="text-slate-400  text-sm">
                      /month {plan.name !== FREE_PLAN_NAME && "+ compute"}
                    </span>
                  </div>
                </CardHeader>

                <CardContent className="pt-0">
                  <ul className="space-y-2 lg:space-y-3 mb-6 lg:mb-8">
                    {features.map((feature, index) => (
                      <li
                        key={index}
                        className="flex items-center gap-2 lg:gap-3"
                      >
                        <Check className="h-3 w-3 lg:h-4 lg:w-4 text-green-400 flex-shrink-0" />
                        <span className="text-xs lg:text-sm text-slate-300 ">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className={`w-full  text-xs lg:text-sm ${
                      isCurrentPlan
                        ? "bg-slate-700 text-slate-400 cursor-not-allowed"
                        : "cyber-button"
                    }`}
                    disabled={isCurrentPlan}
                    onClick={() => handleSelectPlan(plan.id)}
                  >
                    {isCurrentPlan ? "Current Plan" : `Upgrade to ${plan.name}`}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {selectedPlan && (
          <StripeCheckoutDialog
            plan={planQuery.data.find((p) => p.id === selectedPlan)!}
            open={showCheckout}
            onOpenChange={setShowCheckout}
          />
        )}
      </div>
    </div>
  );
}
